/**
 * The URLs a consumer actually calls, one row per deployment environment
 * (task 799).
 *
 * The rest of the Releases surface answers "what is released, and where"; this
 * answers the question an integrator asks next — **"so which URL do I call?"**
 * Environment-qualified addressing (`/api/{slug}/{env}/{path}`) is what makes a
 * request hit exactly what that stage serves, and until now the app never
 * showed it: the Releases surface listed lanes, and the API Docs tab carried one
 * plain server URL.
 *
 * The rows are the environments, in promotion order — the same order the lanes
 * read in. The plain URL is deliberately NOT among them (task 820): while
 * environments exist, the environment-qualified URLs are the ones to integrate
 * against, and a plain URL sitting at the top of the same list invites copying
 * the one address whose meaning depends on how the service is served. Where
 * environments exist, that meaning — "the plain URL serves this one" — is
 * carried by the badge on the environment whose path is `/` (task 831: the row it marks is
 * the bare URL, because that environment has no segment of its own, which is
 * what the retired `Default` badge was really saying); where the account has no
 * environments this list renders nothing at all, and each surface's own bare
 * URL (the Developer API section's call snippet, the API Docs tab's OpenAPI
 * server) is the only URL shown, exactly as before.
 *
 * **THE `/` ROW BEFORE THE FIRST PUBLISH (task 1014).** The serving rule
 * (task 869) is that an AI Service with NO published version serves its working
 * rows at the bare URL, whatever stages the account holds. This list said the
 * opposite on the one row that answers that URL — "Not Deployed", with a hint
 * about promotion — so an integrator reading it concluded the address was dead
 * while it was answering calls. That row now reads **Serving the working
 * state**, behind the indigo accent the Bare URL badge already uses, never the
 * gray inactive bar and never the green serving-a-release family.
 *
 * Only the `/` holder changes. Every other stage genuinely serves nothing: an
 * environment-qualified URL never falls back, it answers `404`, which is
 * exactly what its existing treatment says. And when NO environment holds `/`,
 * nothing new is rendered here at all — the address that serves the working
 * state is then the surface's own bare URL, already shown by the Developer API
 * section's call snippet and by the OpenAPI server, so a row for it would be a
 * second copy of an address this list deliberately does not carry (task 820).
 *
 * An environment with nothing deployed reads as inactive rather than broken:
 * muted name, path and URL behind a left accent bar, labeled "Not Deployed",
 * with one gray line beside the URL saying why there is nothing to call. The
 * Copy affordance stays — the URL is real, and an integrator wiring a stage
 * ahead of its first promotion still wants it.
 *
 * Every URL comes from `utils/endpointUrl`, never composed here, so this can
 * never drift from what the app links, tests and curls elsewhere.
 *
 * Mounted by `ReleasesView` (so both the Advanced endpoint page's Releases tab
 * and the AI Service page's Releases tab get it from one implementation) and by
 * `EndpointApiDocsTab`. It renders NOTHING when the account has no environments
 * or the lever is off, which is what keeps every one of those surfaces looking
 * exactly as it did for accounts that never adopted environments.
 */
import { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useCurrentDeployments, useEnvironments } from '@/hooks/useEnvironments';
import { useVersioningEnabled, useResourceVersions } from '@/hooks/useResourceVersions';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { useOptionalToast } from '@/contexts/ToastContext';
import {
  customDomainUrl,
  environmentDisplayPath,
  environmentInvokeUrl,
} from '@/utils/endpointUrl';
import {
  BARE_URL_ENVIRONMENT_PATH,
  deploymentsByEnvironment,
  versionLabel,
} from '@/types/environments';
import type { Endpoint } from '@/types';
import type { Environment } from '@/types/environments';

interface EnvironmentUrlListProps {
  endpoint: Endpoint;
  className?: string;
}

/**
 * The one line that fills the space beside a not-deployed row's URL. It answers
 * the question the muted row raises ("is this URL wrong?") with the state it is
 * actually in: the address is right, nothing has been promoted to it yet.
 */
const NOT_DEPLOYED_HINT = 'Nothing is served here until a version is promoted to this environment.';

/**
 * The same slot on the `/` row while the AI Service has never been published
 * (task 1014). It answers the question that row raises for the opposite reason:
 * the address is right AND it is answering — with the service as it is now.
 */
const WORKING_STATE_HINT =
  'Not published yet, so the bare URL answers with the service as it is now.';

interface UrlRowProps {
  /** The environment this row addresses. Every row addresses one. */
  environment: Environment;
  label: string;
  meaning: string;
  displayPath: string;
  url: string;
  /**
   * The platform URL, present only when the row's primary URL is a custom
   * domain (task 857) — the address that keeps working whatever the account
   * does with its domains.
   */
  platformUrl?: string | null;
  /** Nothing is promoted here yet — the row reads as inactive. */
  notDeployed: boolean;
  /**
   * This row addresses the bare URL, and the AI Service has no published
   * version — so it is serving its working state (task 1014). Mutually
   * exclusive with `notDeployed`: the address is answering, it is simply not
   * answering with a release.
   */
  workingState: boolean;
  onCopy: (url: string, label: string) => void;
  copied: boolean;
}

function UrlRow({
  environment,
  label,
  meaning,
  displayPath,
  url,
  platformUrl,
  notDeployed,
  workingState,
  onCopy,
  copied,
}: UrlRowProps) {
  return (
    <li
      data-testid="environment-url-row"
      data-environment={environment.name}
      // The accent bar is always in the layout, transparent while the
      // environment serves something, so rows stay aligned either way. The row
      // holds one line from `sm` up so the hint sits in the space beside the
      // URL; below that it wraps under, where there is no space to sit in.
      //
      // The bar's colour is `!important` because the list's `divide-gray-200`
      // sets `border-color` on every separated child through a more specific
      // selector, which would otherwise repaint the bar as the divider grey.
      className={`flex flex-wrap items-start gap-3 border-l-4 px-4 py-3 sm:flex-nowrap ${
        workingState
          ? '!border-l-indigo-500 bg-indigo-50/50 dark:!border-l-indigo-400 dark:bg-indigo-900/10'
          : notDeployed
            ? '!border-l-gray-400 dark:!border-l-gray-500'
            : '!border-l-transparent'
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`font-mono text-sm font-medium ${
              notDeployed ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'
            }`}
          >
            {label}
          </span>
          {/* THE BARE-URL BADGE, WHERE `Default` USED TO BE (task 831). The
              boolean it read is gone; the fact it carried is now a path, and
              `/` is unreadable as a URL segment because it is not one — it says
              this environment has NO segment. The badge is where that gets
              said. */}
          {environment.path === BARE_URL_ENVIRONMENT_PATH && (
            <span
              data-testid="environment-url-bare-badge"
              className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
            >
              Bare URL — no segment
            </span>
          )}
          <span
            // The marker sits on the meaning rather than on the row, because
            // the row already identifies itself as `environment-url-row`.
            data-testid={workingState ? 'environment-url-working-state' : undefined}
            className={
              workingState
                ? 'text-xs font-semibold text-indigo-700 dark:text-indigo-300'
                : 'text-xs text-gray-500 dark:text-gray-400'
            }
          >
            {meaning}
          </span>
        </div>
        <p
          data-testid="environment-url-path"
          className={`mt-1 truncate font-mono text-xs ${
            notDeployed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {displayPath}
        </p>
        <p
          data-testid="environment-url-value"
          className={`mt-0.5 break-all font-mono text-sm ${
            notDeployed ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
          }`}
        >
          {url}
        </p>
        {/* The platform address, beneath the one the account publishes on
            (task 857). Only present when a custom domain is serving — with
            none, the URL above IS the platform URL and this renders nothing. */}
        {platformUrl && (
          <p
            data-testid="environment-url-platform"
            className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
          >
            <span className="font-medium">Platform URL (always available)</span>
            <span className="min-w-0 break-all font-mono">{platformUrl}</span>
            <button
              type="button"
              onClick={() => onCopy(platformUrl, `${label} platform`)}
              data-testid="environment-url-platform-copy"
              aria-label={`Copy ${label} platform URL`}
              className="inline-flex shrink-0 items-center font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              <ClipboardDocumentIcon className="mr-1 h-3.5 w-3.5" />
              Copy
            </button>
          </p>
        )}
      </div>
      {(notDeployed || workingState) && (
        <p
          data-testid="environment-url-hint"
          className="min-w-0 self-center text-xs text-gray-500 dark:text-gray-400 sm:w-56 sm:shrink-0"
        >
          {workingState ? WORKING_STATE_HINT : NOT_DEPLOYED_HINT}
        </p>
      )}
      <button
        type="button"
        onClick={() => onCopy(url, label)}
        data-testid="environment-url-copy"
        className="inline-flex flex-shrink-0 items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
      >
        {copied ? (
          <CheckIcon className="mr-1 h-4 w-4 text-green-500" />
        ) : (
          <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
        )}
        Copy
      </button>
    </li>
  );
}

export function EnvironmentUrlList({ endpoint, className }: EnvironmentUrlListProps) {
  // Same lever as the rest of the environments feature. The flag keeps its
  // original `…rollout` name (task 799 decision): renaming a live flag would
  // touch the manifest, Unleash and the generated types for no behavior change.
  const featureEnabled = useFlag('innovate.fe.environments.rollout');
  const { environments } = useEnvironments({ enabled: featureEnabled });
  const { data: current = [] } = useCurrentDeployments(endpoint.id, { enabled: featureEnabled });
  // The hostname these URLs lead with, when the account is serving on one
  // (task 857). None → every row is exactly the platform URL it always was.
  const customDomains = useActiveCustomDomainHostnames({ enabled: featureEnabled });
  const primaryHostname = customDomains[0] ?? null;
  // What the bare URL answers with (task 1014). `isLoading` matters as much as
  // the data: the versions read only starts once the account setting resolves,
  // so treating the empty first render as "never published" would flash the
  // working-state wording at every published service.
  const { enabled: publishingEnabled } = useVersioningEnabled();
  const { data: versions = [], isLoading: versionsLoading } = useResourceVersions(
    'endpoint',
    endpoint.id,
    { enabled: featureEnabled }
  );
  const unpublished =
    publishingEnabled &&
    !versionsLoading &&
    versions.filter((version) => version.status === 'published').length === 0;
  const toast = useOptionalToast();
  const [copied, setCopied] = useState<string | null>(null);

  if (!featureEnabled || environments.length === 0) return null;

  const currentByEnvironment = deploymentsByEnvironment(current);

  const handleCopy = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(label);
      toast?.success(`${label} URL copied to clipboard`, url);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast?.error('Failed to copy');
    }
  };

  return (
    <div
      data-testid="environment-url-list"
      className={`rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 ${
        className ?? ''
      }`}
    >
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">URLs</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Naming an environment in the URL calls exactly what that environment serves.
        </p>
      </div>

      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {environments.map((environment) => {
          const servingVersion = currentByEnvironment[environment.id]?.resourceVersion;
          const workingState = unpublished && environment.path === BARE_URL_ENVIRONMENT_PATH;
          const notDeployed = !servingVersion && !workingState;
          const platformUrl = environmentInvokeUrl(endpoint, environment.path);

          return (
            <UrlRow
              key={environment.id}
              environment={environment}
              label={environment.name}
              meaning={
                servingVersion
                  ? `Serving v${versionLabel(servingVersion)}`
                  : workingState
                    ? 'Serving the working state'
                    : 'Not Deployed'
              }
              displayPath={environmentDisplayPath(endpoint, environment.path)}
              url={
                primaryHostname
                  ? customDomainUrl(endpoint, primaryHostname, { environment: environment.path })
                  : platformUrl
              }
              platformUrl={primaryHostname ? platformUrl : null}
              notDeployed={notDeployed}
              workingState={workingState}
              onCopy={handleCopy}
              copied={copied === environment.name}
            />
          );
        })}
      </ul>
    </div>
  );
}

export default EnvironmentUrlList;
